export const SERVER_URL = 'http://localhost:8080';
const BASE_URL = `${SERVER_URL}/api`;

const authHeader = () => {
  const token = localStorage.getItem('locallens_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const jsonHeaders = () => ({ 'Content-Type': 'application/json', ...authHeader() });

// Turns backend {"error": "..."} responses into real Error messages
const handle = async (response, fallback) => {
  if (!response.ok) {
    let message = fallback;
    try {
      const err = await response.json();
      message = err.error || fallback;
    } catch (e) {}
    throw new Error(message);
  }
  return response.json();
};

export const getAllPosts = async () => {
  const response = await fetch(`${BASE_URL}/posts`);
  return response.json();
};

export const getMergedPosts = async () => {
  const response = await fetch(`${BASE_URL}/posts/merged`);
  return response.json();
};

export const createPost = async (post) => {
  const response = await fetch(`${BASE_URL}/posts`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(post)
  });
  return handle(response, 'Could not create post');
};

export const upvotePost = async (id) => {
  const response = await fetch(`${BASE_URL}/posts/${id}/upvote`, {
    method: 'PUT',
    headers: authHeader()
  });
  return handle(response, 'Could not upvote');
};

export const confirmPost = async (id) => {
  const response = await fetch(`${BASE_URL}/posts/${id}/confirm`, {
    method: 'PUT',
    headers: authHeader()
  });
  return handle(response, 'Could not confirm');
};

export const denyPost = async (id) => {
  const response = await fetch(`${BASE_URL}/posts/${id}/deny`, {
    method: 'PUT',
    headers: authHeader()
  });
  return handle(response, 'Could not deny');
};

export const deletePost = async (id) => {
  const response = await fetch(`${BASE_URL}/posts/${id}`, {
    method: 'DELETE',
    headers: authHeader()
  });
  if (!response.ok) throw new Error('Could not delete');
};

export const editPost = async (id, post) => {
  const response = await fetch(`${BASE_URL}/posts/${id}`, {
    method: 'PUT',
    headers: jsonHeaders(),
    body: JSON.stringify(post)
  });
  return handle(response, 'Could not edit post');
};

export const getComments = async (postId) => {
  const response = await fetch(`${BASE_URL}/comments/post/${postId}`);
  return response.json();
};

export const addComment = async (postId, text) => {
  const response = await fetch(`${BASE_URL}/comments/post/${postId}`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ text })
  });
  return handle(response, 'Could not add comment');
};

// source = 'camera' (captured live in the app) or 'gallery' (uploaded file)
export const uploadPostImage = async (postId, file, source) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('source', source);

  const response = await fetch(`${BASE_URL}/posts/${postId}/photo`, {
    method: 'POST',
    headers: authHeader(),
    body: formData
  });
  return handle(response, 'Image upload failed');
};

// Works for old posts ("/uploads/x.jpg") and new ones - always loads via the API
export const imageSrc = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  const fileName = url.split('/').pop();
  return `${BASE_URL}/posts/photo/${fileName}`;
};

// ---------- AREA FEATURES ----------

// New reports in the user's city since their last visit (since = serverTime we stored earlier)
export const getAreaAlerts = async (city, since) => {
  const params = new URLSearchParams({ city });
  if (since) params.append('since', since);
  const response = await fetch(`${BASE_URL}/posts/alerts?${params.toString()}`, {
    headers: authHeader()
  });
  return handle(response, 'Could not load alerts');
};

// The saved profile (name, email, city) from the database
export const fetchMyProfile = async () => {
  const response = await fetch(`${BASE_URL}/user/me`, { headers: authHeader() });
  return handle(response, 'Could not load profile');
};

// Save the city permanently in the database
export const saveMyCity = async (city) => {
  const response = await fetch(`${BASE_URL}/user/city`, {
    method: 'PUT',
    headers: jsonHeaders(),
    body: JSON.stringify({ city })
  });
  return handle(response, 'Could not save city');
};